import React, { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGallery } from '../context/GalleryContext';
import { Seo } from '../components/seo/Seo';
import { ViewingRoom } from '../components/viewing-room/ViewingRoom';
import { MEDIUM_DISPLAY } from '../types/Medium';
import { YEAR_DISPLAY } from '../types/Year';
import { SITE_NAME, SITE_URL } from '../constants';

// Resolves the current artwork within its section, drives wrap-around prev/next (buttons +
// arrow keys), and emits per-route SEO (canonical → the artwork's primary section, plus
// JSON-LD VisualArtwork). Presentation is delegated to <ViewingRoom>.
export const ViewingRoomPage = ({ sectionPath, artworkSlug }: { sectionPath: string[]; artworkSlug: string }) => {
    const { getMembership, getArtwork, getPrimarySectionPath, viewingRoom } = useGallery();
    const navigate = useNavigate();

    const works = getMembership(sectionPath);
    const index = works.findIndex((a) => a.slug === artworkSlug);
    const artwork = getArtwork(artworkSlug)!;

    // Neighbouring works (wrap-around) — their images are warmed below so prev/next swaps instantly.
    const neighbourSrc = (delta: number): string | undefined => {
        if (!works.length) return undefined;
        const w = works[(index + delta + works.length) % works.length];
        return w.srcWebp ?? w.src;
    };
    const prevSrc = neighbourSrc(-1);
    const nextSrc = neighbourSrc(1);

    const go = useCallback(
        (delta: number) => {
            if (!works.length) return;
            const next = (index + delta + works.length) % works.length;
            navigate(`/${sectionPath.join('/')}/${works[next].slug}`);
        },
        [index, works, sectionPath, navigate],
    );

    const onPrev = useCallback(() => go(-1), [go]);
    const onNext = useCallback(() => go(1), [go]);

    // Arrow-key navigation — guarded against modifiers and typing in form fields.
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey) return;
            const target = e.target as HTMLElement | null;
            if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return;
            if (e.key === 'ArrowLeft') onPrev();
            else if (e.key === 'ArrowRight') onNext();
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onPrev, onNext]);

    // Warm the browser cache for the previous/next images so the viewing-room <img> (which remounts
    // per artwork) has the next piece ready — instant swap, no blank frame. The fetch populates the
    // cache even if the Image is garbage-collected.
    useEffect(() => {
        for (const src of [prevSrc, nextSrc]) {
            if (!src) continue;
            const img = new Image();
            img.src = src;
        }
    }, [prevSrc, nextSrc]);

    // Canonical URL: the artwork's primary section (falls back to the current path).
    const primary = getPrimarySectionPath(artwork.slug) ?? sectionPath;
    const canonicalPath = `/${primary.join('/')}/${artwork.slug}`;
    const imageUrl = `${SITE_URL}${artwork.srcWebp ?? artwork.src}`;

    const jsonLd = {
        '@context': 'https://schema.org',
        '@type': 'VisualArtwork',
        name: artwork.title,
        creator: { '@type': 'Person', name: SITE_NAME },
        artMedium: MEDIUM_DISPLAY[artwork.medium],
        dateCreated: YEAR_DISPLAY[artwork.year],
        image: imageUrl,
        url: `${SITE_URL}${canonicalPath}`,
    };

    return (
        <>
            <Seo
                title={artwork.title}
                description={`${artwork.title} — ${MEDIUM_DISPLAY[artwork.medium]}, ${YEAR_DISPLAY[artwork.year]}. Original artwork by ${SITE_NAME}.`}
                canonicalPath={canonicalPath}
                image={artwork.srcWebp ?? artwork.src}
                jsonLd={jsonLd}
            />
            <ViewingRoom artwork={artwork} mode={viewingRoom} sectionPath={sectionPath} onPrev={onPrev} onNext={onNext} />
        </>
    );
};
